import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  FileSignature, Send, ShieldCheck, Clock, CheckCircle2,
  Copy, XCircle, Loader2, RefreshCw, Phone, FileDown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Card firma OTP contratto. Mostra lo stato attuale e permette:
 * - Richiesta firma (invia OTP via email)
 * - Visualizza audit trail (hash, IP, timestamp) se firmato
 */
const ContractFirmaCard = ({ contractId, clientEmail, clientTelefono }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [emailOverride, setEmailOverride] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [lastOtpDebug, setLastOtpDebug] = useState(null);
  // Stato per la verifica OTP dettato dal cliente
  const [otpInput, setOtpInput] = useState('');
  const [consensoOperatore, setConsensoOperatore] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/firma/status/${contractId}`);
      setStatus(res.data);
    } catch (e) {
      setStatus({});
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleRichiedi = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/firma/richiedi`, {
        contract_id: contractId,
        email_cliente: emailOverride.trim() || undefined,
      });
      toast.success(res.data.email_sent ? 'Email inviata al cliente' : 'Richiesta creata (email non inviata: verificare configurazione Resend)');
      setLastLink(res.data.firma_url);
      setLastOtpDebug(res.data.otp_debug);
      setShowForm(false);
      setEmailOverride('');
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio firma');
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (!lastLink) return;
    navigator.clipboard.writeText(lastLink);
    toast.success('Link copiato');
  };

  const handleVerificaOperatore = async () => {
    if (!/^\d{6}$/.test(otpInput)) return toast.error('Il codice OTP deve essere di 6 cifre');
    if (!consensoOperatore) return toast.error('Devi confermare che il cliente ha dato consenso verbale');
    setVerifying(true);
    try {
      const res = await axios.post(`${API_URL}/api/firma/verifica-operatore`, {
        contract_id: contractId,
        otp: otpInput.trim(),
        consenso_verbale_confermato: consensoOperatore,
      });
      toast.success('Firma registrata con successo');
      setOtpInput('');
      setConsensoOperatore(false);
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore verifica OTP');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) return null;

  const isFirmato = status?.status === 'firmato';
  const isPending = status?.status === 'pending';
  const isScaduto = status?.status === 'scaduto' || status?.status === 'annullato';
  const isNoRequest = !status?.status;

  return (
    <Card data-testid="firma-card" className={isFirmato ? 'border-emerald-200 bg-emerald-50/30' : isPending ? 'border-sky-200 bg-sky-50/30' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileSignature className="w-5 h-5 text-sky-600" />
          Firma OTP Cliente
          {isFirmato && <Badge className="bg-emerald-500 text-white ml-auto">Firmato</Badge>}
          {isPending && <Badge className="bg-sky-500 text-white ml-auto">In attesa</Badge>}
          {isScaduto && <Badge className="bg-slate-400 text-white ml-auto">Scaduto</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isFirmato ? (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-medium">Cliente ha firmato il {status.firmato_at ? new Date(status.firmato_at).toLocaleString('it-IT') : ''}</span>
            </div>
            {status.signed_data && (
              <div className="text-xs text-slate-600 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 mt-2">
                <span className="text-slate-400">Firmatario:</span>
                <span>{status.signed_data.nome} {status.signed_data.cognome}</span>
                {status.signed_data.codice_fiscale && <>
                  <span className="text-slate-400">CF:</span>
                  <span className="font-mono">{status.signed_data.codice_fiscale}</span>
                </>}
                <span className="text-slate-400">IP:</span>
                <span className="font-mono">{status.firma_ip}</span>
              </div>
            )}
            {status.firma_hash && (
              <details className="text-xs text-slate-500 mt-2">
                <summary className="cursor-pointer">Codice firma (audit trail)</summary>
                <p className="font-mono break-all mt-1 p-2 bg-slate-100 rounded">{status.firma_hash}</p>
              </details>
            )}
            {status.pdf_url && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => window.open(`${API_URL}${status.pdf_url}`, '_blank')}
                className="mt-2 text-sky-700 border-sky-200 hover:bg-sky-50"
                data-testid="firma-pdf-download-btn"
              >
                <FileDown className="w-3.5 h-3.5 mr-1" /> Scarica PDF firmato con timbro digitale
              </Button>
            )}
          </div>
        ) : isPending ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-sky-700">
              <Clock className="w-4 h-4" />
              <span>OTP inviato a <strong>{status.cliente_email}</strong> - scade {status.expires_at ? new Date(status.expires_at).toLocaleString('it-IT') : ''}</span>
            </div>

            {/* Form operatore: il cliente detta l'OTP al telefono */}
            <div className="rounded-xl bg-white border-2 border-sky-200 p-4 space-y-3" data-testid="firma-verifica-operatore">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-sky-600" />
                <p className="text-sm font-semibold text-sky-800">Il cliente ti sta dettando l'OTP al telefono?</p>
              </div>
              <div>
                <Label htmlFor="firma-otp-op" className="text-xs">Codice OTP dettato dal cliente</Label>
                <Input
                  id="firma-otp-op"
                  type="text"
                  inputMode="numeric"
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="text-center font-mono text-2xl tracking-widest h-12 mt-1"
                  maxLength={6}
                  data-testid="firma-otp-operatore-input"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={consensoOperatore}
                  onCheckedChange={(v) => setConsensoOperatore(!!v)}
                  className="mt-0.5"
                  data-testid="firma-consenso-verbale"
                />
                <span className="text-xs text-slate-700 leading-relaxed">
                  Confermo che il cliente <strong>{status.cliente_email}</strong> ha dato consenso verbale al trattamento
                  dei dati (GDPR) e accettato le condizioni contrattuali di fornitura.
                </span>
              </label>
              <Button
                type="button"
                onClick={handleVerificaOperatore}
                disabled={verifying || !otpInput || !consensoOperatore}
                className="w-full h-10 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold"
                data-testid="firma-conferma-operatore-btn"
              >
                {verifying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registro firma…</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Conferma firma per il cliente</>}
              </Button>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                Cliccando "Conferma" registri la firma per conto del cliente. L'audit trail salva il tuo ID operatore,
                IP, timestamp e hash dei dati. Il cliente puo' anche firmare autonomamente dal link ricevuto via email.
              </p>
            </div>

            <Button
              type="button" size="sm" variant="outline"
              onClick={() => setShowForm(true)}
              className="w-full"
              data-testid="firma-resend-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reinvia OTP con nuovo codice
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {isScaduto && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5" /> L'ultima richiesta è scaduta o annullata.
              </p>
            )}
            {isNoRequest && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Invia una richiesta OTP via email al cliente per confermare identità e consenso.
              </p>
            )}
            {!showForm && (
              <Button
                type="button" size="sm"
                onClick={() => setShowForm(true)}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="firma-richiedi-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1" /> Richiedi firma OTP
              </Button>
            )}
            {showForm && (
              <div className="space-y-2 p-3 bg-white rounded-xl border border-slate-200" data-testid="firma-form-inline">
                <Label htmlFor="firma-email" className="text-xs">Email cliente</Label>
                <Input
                  id="firma-email"
                  type="email"
                  value={emailOverride || clientEmail || ''}
                  onChange={(e) => setEmailOverride(e.target.value)}
                  placeholder={clientEmail || 'email@esempio.it'}
                  data-testid="firma-email-input"
                />
                <p className="text-[10px] text-slate-500">Modificabile solo per questo invio. Aggiornare in anagrafica cliente se cambia definitivamente.</p>
                <div className="flex gap-2 mt-1">
                  <Button
                    type="button" size="sm"
                    onClick={handleRichiedi}
                    disabled={sending}
                    className="bg-sky-600 hover:bg-sky-700 text-white"
                    data-testid="firma-invia-btn"
                  >
                    {sending ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Invio…</> : <><Send className="w-3.5 h-3.5 mr-1" /> Invia OTP</>}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowForm(false)}>Annulla</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {lastLink && (
          <div className="mt-2 p-2 bg-slate-100 rounded-lg text-xs">
            <div className="flex items-center gap-1 text-slate-500 mb-1">
              Link firma:
              <button type="button" onClick={copyLink} className="ml-auto text-sky-600 hover:text-sky-700 flex items-center gap-1">
                <Copy className="w-3 h-3" /> Copia
              </button>
            </div>
            <p className="font-mono text-[10px] break-all text-slate-600">{lastLink}</p>
            {lastOtpDebug && (
              <p className="mt-1 text-amber-700 text-[11px]">
                <strong>OTP di test (Resend non attivo):</strong> {lastOtpDebug}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ContractFirmaCard;
